import { useMemo, useCallback } from 'react';
import { ImmutableRowsMap, InternalGridData, RowsMap } from './grid-types';
import { UpdateCellInfo } from './contexts/gridContext';
import produce, { enableMapSet } from 'immer';
import { useControlledState } from '../../../hooks/useControlledState';
import { filterMapMap, findIndexInMap, findMap, mapMapToArray, reduceMap, someMap } from './helpers/map';
import { GridRow, GridData, GridCell } from '../../../api/generated/data-contracts';
import { useRecoilCallback, Snapshot, RecoilState, useRecoilState, useRecoilValue, atomFamily, MutableSnapshot, selectorFamily } from 'recoil';
import Big from 'big.js';
import { useEvent } from '../../../hooks/useEvent';
import { reduceMapAsync } from './helpers/mapAsync';
import { useGridActionHistory, CellSetter, runVoidCallbackAsync } from './useGridActionHistory';
import { devLogger } from '../../../helpers/devLogger';

enableMapSet();

type CellValueGetter = (cell: GridCell) => Promise<Big>;
type CellValueSetter = (cell: GridCell, valOrUpdater: Big | ((currentValue: Big) => Big)) => void;

export type RowUpdateInfo = UpdateCellInfo & {
    diff: Big;
    row: Readonly<GridRow>;
    getCellValue: CellValueGetter;
    setCellValue: CellValueSetter;
    totalRows: readonly Readonly<GridRow>[];
    rows: ImmutableRowsMap;
    getRowRelationsToRoot: (rowId: string) => readonly Readonly<GridRow>[];
};

type RowUpdater = (info: RowUpdateInfo) => Promise<void> | void;

export type RowTreeUpdater = (moveInfo: UpdateRowTreeInfo) => Promise<RowsMap | ImmutableRowsMap> | RowsMap | ImmutableRowsMap;

export type RowDeleteInfo = {
    row: GridRow;
    rowsObject: Readonly<RowsMap>;
    totalRows: GridRow[];
    getCellValue: CellValueGetter;
    setCellValue: CellValueSetter;
};

export type AddRowInfo = {
    ancestors: GridRow[];
    rows: RowsMap;
    parentRow?: GridRow;
    /**
     * Immediately aborts the operation and stops code execution where called
     * @param reason - reason for aborting the operation
     * @returns
     */
    abort: (reason?: string) => void;
    /**
     * Useful for lazy loading metadata and update the row later.
     * This function can only be executed once, and throws on further attempts
     */
    updateRows: (...updateRows: GridRow[]) => Promise<void>;
};

type RowsGetter = (rowId: string) => Readonly<GridRow>[];

export type UpdateRowTreeInfo = {
    getSubrows: RowsGetter;
    getSubrowFamily: RowsGetter;
    getRowAncestors: RowsGetter;
    findRow: (cb: (row: Readonly<GridRow>) => boolean) => Readonly<GridRow>;
    findRowIndex: (cb: (row: Readonly<GridRow>) => boolean) => number;
    getRowIndex: (rowId: string) => number;
    getCellValue: CellValueGetter;
    setCellValue: CellValueSetter;
    abortMove: () => void;
    rowsMap: ImmutableRowsMap;
    rowAncestors: Readonly<GridRow>[];
    subrows: Readonly<GridRow>[];
    subrowFamily: Readonly<GridRow>[];
    rowToMove: Readonly<GridRow>;
    newParentIndex: number | null;
    newParentId: string | null;
};

export type OnMoveRowInfo = {
    rowToMove: GridRow;
    rowAncestors: GridRow[];
    subrows: GridRow[];
    subrowFamily: GridRow[];

    newRowAncestors: GridRow[];
    newSubrows: GridRow[];
    newSubrowFamily: GridRow[];
};

export type ExpandedState = {
    [key: string]: boolean;
};

export type VisibleRow = {
    row: Readonly<GridRow>;
    visible: boolean;
    nestingLevel: number;
    expanded: boolean;
    subrows: GridRow[];
    rowRelations: GridRow[];
};

export const getRootRows = (rows: GridRow[]) => rows.filter(row => !row.parentRowId);

const cleanRows = (rows: GridRow[]) => {
    const rootRows = getRootRows(rows);
    const getFamilySubRowsOfRow = (rowId: string) => {
        const subRowFamily: GridRow[] = [];
        const recurse = (rowId: string) => {
            const subRows = rows.filter(row => {
                return row.parentRowId === rowId;
            });
            subRows.forEach(subRow => {
                subRowFamily.push(subRow);
                recurse(subRow.id);
            });
        };
        recurse(rowId);
        return subRowFamily;
    };
    return rootRows.reduce((acc, row) => {
        acc.push(row, ...getFamilySubRowsOfRow(row.id));
        return acc;
    }, [] as GridRow[]);
};

const prepareGridData = (data: GridData, clean?: boolean): InternalGridData => {
    return {
        ...data,
        originalRows: data.rows,
        rows: new Map((clean ? cleanRows(data.rows) : data.rows).map(row => [row.id, row])),
    };
};

const getRowRelationsToRoot = (rowId: string, rows: RowsMap) => {
    const tree: GridRow[] = [];
    let currentAncestor = rows.get(rowId);
    while (currentAncestor) {
        currentAncestor = rows.get(currentAncestor.parentRowId);
        if (currentAncestor) {
            tree.push(currentAncestor);
        }
    }
    return tree;
};

const getFamilySubRowsOfRow = (rowId: string, rows: RowsMap) => {
    const subRowFamily: GridRow[] = [];
    const recurse = (rowId: string) => {
        const subRows = filterMapMap(rows, row => {
            if (row.parentRowId === rowId) {
                return row;
            }
        });
        subRows.forEach(subRow => {
            subRowFamily.push(subRow);
            recurse(subRow.id);
        });
    };
    recurse(rowId);
    return subRowFamily;
};

const getSubRowsOfRow = (rowId: string, rows: RowsMap) => {
    const subRows: GridRow[] = [];
    rows.forEach(row => {
        if (row.parentRowId === rowId) {
            subRows.push(row);
        }
    });
    return subRows;
};

// const cleanUpParentRows = async (
//     rowId: string,
//     rows: RowsMap,
//     ancestors: GridRow[],
//     onShouldDeleteRow?: (row: GridRow, rows: RowsMap) => Promise<boolean> | boolean,
// ) => {
//     for (const ancestor of ancestors) {
//         const siblingRows = getSubRowsOfRow(ancestor.id, rows).filter(an => an.id !== rowId);
//         if (siblingRows.length === 0) {
//             if (onShouldDeleteRow) {
//                 if (!(await onShouldDeleteRow(rows.get(ancestor.id), rows))) {
//                     return;
//                 }
//             }
//             rows.delete(ancestor.id);
//         }
//     }
// };

export const getCellsSelector = selectorFamily<Big[], Readonly<GridCell>[]>({
    key: 'gridCells',
    get:
        cells =>
        ({ get }) => {
            return cells.map(cell => get(getCellAtom(cell)));
        },
});

const getCellAtom = atomFamily<Big, Readonly<GridCell>>({
    key: 'gridCell',
    default: cell => {
        const { value } = cell || {};
        return new Big((value || 0).toString());
    },
});

export const createCellValueGetter = (snapshot: Snapshot | MutableSnapshot) => (cell: GridCell) => snapshot.getPromise(getCellAtom(cell));
export const createCellValueSetter =
    (set: (recoilVal: RecoilState<Big>, valOrUpdater: Big | ((currVal: Big) => Big)) => void) =>
    (cell: GridCell, valOrUpdater: ((currentValue: Big) => Big) | Big) =>
        set(getCellAtom(cell), valOrUpdater);

export const createCellValueSetGet = (snapshot: MutableSnapshot) => {
    return {
        setCellValue: createCellValueSetter(snapshot.set),
        getCellValue: createCellValueGetter(snapshot),
    };
};

export const useGridCellState = (cell: GridCell) => useRecoilState(getCellAtom(cell));
export const useGridCellValue = (cell: GridCell) => useRecoilValue(getCellAtom(cell));

export const useGetCellValue = () =>
    useRecoilCallback(
        ({ snapshot }) =>
            async (cell: GridCell) => {
                const getter = createCellValueGetter(snapshot);
                return getter(cell);
            },
        [],
    );

export type OnUpdateCellInfo = UpdateCellInfo & {
    relations: GridRow[];
    row: GridRow;
    diff: Big;
};

export type UseGridProps<AddRowMetadata = any> = {
    data: GridData;
    onUpdateCells?: (infos: OnUpdateCellInfo[]) => Promise<void> | void;
    onUpdateCellTree?: RowUpdater;
    onAddRow?: (addRowInfo: AddRowInfo, metadata: AddRowMetadata, placeLast?: boolean) => Promise<GridRow | GridRow[] | void> | GridRow | GridRow[] | void;
    onUpdateRowTree?: RowTreeUpdater;
    onMoveRow?: (info: OnMoveRowInfo) => Promise<void> | void;
    autoUpdateTotals?: boolean;
    readonly?: boolean;
    /**
     * Tries to clean up rows that are out of order
     * defaults to false
     */
    cleanRows?: boolean;
    defaultExpandedState?: boolean;
};

export const useGrid = <AddRowMetadata = any>({
    data: incomingData,
    onUpdateCells,
    onUpdateCellTree,
    onAddRow,
    autoUpdateTotals = true,
    readonly = false,
    onUpdateRowTree,
    onMoveRow,
    cleanRows: cleanRowsProps,
    defaultExpandedState = false,
}: UseGridProps<AddRowMetadata>) => {
    const [data, setData] = useControlledState(() => prepareGridData(incomingData, cleanRowsProps), [incomingData, cleanRowsProps]);
    const { name, headers, rows, metadata, id } = data;

    const { addAction, unstable_quietlySpliceUndoStack } = useGridActionHistory();

    const setRootRows = useCallback(
        async (cb: (rootRows: GridRow[]) => GridRow[] | Promise<GridRow[]>) => {
            const allRows = [...rows.values()];
            const rootRows = getRootRows(allRows);
            const newRootRows = await cb(rootRows);
            const newRows = newRootRows.flatMap(row => [row, ...getFamilySubRowsOfRow(row.id, rows)]);
            setData(data => ({ ...data, rows: new Map(newRows.map(row => [row.id, row])) }));
        },
        [rows, setData],
    );

    const [expandedRows, setExpandedRows] = useControlledState<ExpandedState>(
        lastState => {
            const newState = {
                ...incomingData.rows.reduce((acc, row) => {
                    // acc[row.id] = false;
                    acc[row.id] = defaultExpandedState;
                    return acc;
                }, {} as ExpandedState),
                ...lastState,
            };
            return newState;
        },
        [incomingData.rows, defaultExpandedState],
    );

    const getRowAncestorTreeToRoot = useCallback(
        (rowId: string) => {
            return getRowRelationsToRoot(rowId, rows);
        },
        [rows],
    );

    const getSubRows = useCallback(
        (rowId: string) => {
            return getFamilySubRowsOfRow(rowId, rows);
        },
        [rows],
    );

    const updateCellValue = useRecoilCallback(
        ({ snapshot, set }) =>
            async (updatesOrUpdate: UpdateCellInfo | UpdateCellInfo[], shouldCallHandler = true) => {
                if (readonly) {
                    throw new Error('Grid is set to readonly');
                }
                const updates = Array.isArray(updatesOrUpdate) ? updatesOrUpdate : [updatesOrUpdate];
                let err: any = null;
                try {
                    const undoUpdateInfos: OnUpdateCellInfo[] = [];
                    const updateInfos: OnUpdateCellInfo[] = [];

                    const getCellValue = createCellValueGetter(snapshot);

                    for (const { cell, cellIndex, columnIndex, newValue, rowId } of updates) {
                        const totalRows = getRowRelationsToRoot(rowId, rows);
                        const currentCellValue = await getCellValue(cell);
                        const diff = newValue.minus(currentCellValue);

                        const row = rows.get(rowId);

                        const info: OnUpdateCellInfo = {
                            cell,
                            cellIndex,
                            columnIndex,
                            newValue,
                            rowId,
                            relations: totalRows,
                            row,
                            diff,
                        };

                        updateInfos.push(info);
                        undoUpdateInfos.push({
                            ...info,
                            newValue: currentCellValue,
                            diff: diff.neg(),
                        });
                    }

                    const setNextState = async (snapshot: Snapshot, set: CellSetter, updateInfos: OnUpdateCellInfo[]) => {
                        const setCellValue = createCellValueSetter(set);
                        const getCellValue = createCellValueGetter(snapshot);
                        for (const { cell, cellIndex, columnIndex, newValue, relations, row, rowId, diff } of updateInfos) {
                            if (onUpdateCellTree) {
                                await onUpdateCellTree({
                                    cell,
                                    cellIndex,
                                    columnIndex,
                                    diff,
                                    newValue,
                                    row,
                                    rowId,
                                    totalRows: relations,
                                    getCellValue,
                                    setCellValue,
                                    rows,
                                    getRowRelationsToRoot: rowId => getRowRelationsToRoot(rowId, rows),
                                });
                            }
                            setCellValue(cell, newValue);
                            if (autoUpdateTotals) {
                                for (const totalRow of relations) {
                                    const totalCell = rows.get(totalRow.id)?.columnRows?.[columnIndex]?.cells?.[cellIndex];
                                    if (totalCell) {
                                        setCellValue(totalCell, value => value.plus(diff));
                                    }
                                }
                            }
                        }
                    };
                    await setNextState(snapshot, set, updateInfos);
                    addAction(
                        async (snapshot, set) => {
                            await setNextState(snapshot, set, undoUpdateInfos);
                            if (onUpdateCells && shouldCallHandler) {
                                await onUpdateCells(undoUpdateInfos);
                            }
                        },
                        async (snapshot, set) => {
                            await setNextState(snapshot, set, updateInfos);
                            if (onUpdateCells && shouldCallHandler) {
                                await onUpdateCells(updateInfos);
                            }
                        },
                    );
                    if (onUpdateCells && shouldCallHandler) {
                        err = await runVoidCallbackAsync(async () => {
                            await onUpdateCells(updateInfos);
                        });
                    }
                } catch (error) {
                    err = error;
                }
                if (err) {
                    throw err;
                }
            },
        [addAction, autoUpdateTotals, onUpdateCellTree, onUpdateCells, readonly, rows],
    );

    const addRow = useCallback(
        async (parentRowId: string | null, metaData: AddRowMetadata, placeLast = true) => {
            if (!onAddRow) {
                throw new Error('A callback to adding row was not provided');
            }
            const currentData = data;
            const currentRows = currentData.rows;
            const parentRow = currentRows.get(parentRowId);
            const ancestors = getRowRelationsToRoot(parentRowId, currentRows);
            let updated = false;

            /**
             * This is designed to be called later, useful for e.g lazy loading metadata
             */
            const updateRows = async (...rows: GridRow[]) => {
                if (updated) {
                    throw new Error('ILLEGAL FUNCTION CALL: updateRows can only be called 1 time');
                }
                updated = true;
                if (abortAddOperation) {
                    return;
                }
                const updatedData = await produce(newData, async data => {
                    const currentRows = data.rows;
                    data.rows = await reduceMapAsync(
                        currentRows,
                        async (_rows, _row, index, key) => {
                            const updatedRow = rows.find(row => _row.id === row.id);
                            if (updatedRow) {
                                _rows.set(updatedRow.id, updatedRow);
                            } else {
                                _rows.set(key, _row);
                            }
                            return _rows;
                        },
                        new Map() as Map<string, GridRow>,
                    );
                });

                unstable_quietlySpliceUndoStack(actionIndex, 1, [
                    async () => {
                        setData(currentData);
                    },
                    async () => {
                        setData(updatedData);
                    },
                ]);
                setData(updatedData);
            };

            let abortAddOperation: string | boolean = false;
            const abort = (reason?: string) => {
                throw new AbortRowOperation(reason || 'Abort add operation');
            };
            let newRow: GridRow | GridRow[] | void;
            try {
                newRow = await onAddRow({ ancestors, rows: currentRows, parentRow, abort, updateRows }, metaData);
            } catch (e) {
                if (e instanceof AbortRowOperation) {
                    abortAddOperation = e.message || true;
                } else {
                    throw e;
                }
            }
            if (abortAddOperation || !newRow) {
                devLogger.log('Add row was aborted early: ' + (typeof abortAddOperation === 'string' ? abortAddOperation : ''));
                return {
                    newRows: Array.from(currentRows.values()),
                    parentRow,
                };
            }
            const newRows = Array.isArray(newRow) ? newRow : [newRow];
            const newData = !parentRowId
                ? produce(currentData, data => {
                      let insertAfter = false;
                      let hasInserted = false;
                      const rows = data.rows;
                      data.rows = reduceMap(
                          rows,
                          placeLast
                              ? (acc, row, index, key) => {
                                    if (row.id === parentRowId) {
                                        insertAfter = true;
                                    }
                                    if (insertAfter && row.id !== parentRowId && row.parentRowId !== parentRowId && !hasInserted) {
                                        newRows.forEach(row => {
                                            acc.set(row.id, row);
                                        });
                                        hasInserted = true;
                                        insertAfter = false;
                                    }
                                    acc.set(key, row);
                                    return acc;
                                }
                              : (acc, row, index, key) => {
                                    if (row.id === parentRowId || !hasInserted) {
                                        newRows.forEach(row => {
                                            acc.set(row.id, row);
                                        });
                                        hasInserted = true;
                                    }
                                    acc.set(key, row);
                                    return acc;
                                },
                          new Map() as Map<string, GridRow>,
                      );
                  })
                : produce(currentData, data => {
                      let insertAfter = false;
                      const rows = data.rows;
                      data.rows = reduceMap(
                          rows,
                          placeLast
                              ? (acc, row, index, key) => {
                                    if (row.id === parentRowId) {
                                        insertAfter = true;
                                    }
                                    // if (insertAfter && row.id !== parentRowId && (row.parentRowId !== parentRowId || index === rows.size - 1)) {
                                    if (insertAfter && row.id !== parentRowId && row.parentRowId !== parentRowId) {
                                        newRows.forEach(row => {
                                            acc.set(row.id, row);
                                        });
                                        insertAfter = false;
                                    }

                                    acc.set(key, row);
									
									if (insertAfter && index === rows.size - 1) {
										newRows.forEach(row => {
											acc.set(row.id, row);
										});
										insertAfter = false;
									}

                                    return acc;
                                }
                              : (acc, row, index, key) => {
                                    acc.set(key, row);
                                    if (row.id === parentRowId) {
                                        newRows.forEach(row => {
                                            acc.set(row.id, row);
                                        });
                                    }
                                    return acc;
                                },
                          new Map() as Map<string, GridRow>,
                      );
                  });
            setData(newData);
            const actionIndex = await addAction(
                async () => {
                    setData(currentData);
                },
                async () => {
                    setData(newData);
                },
            );
            return {
                newRows,
                parentRow,
            };
        },
        [addAction, data, onAddRow, setData, unstable_quietlySpliceUndoStack],
    );

    const moveRow = useRecoilCallback(
        ({ snapshot, gotoSnapshot }) =>
            async (rowId: string, newParentId: string | null = null) => {
                // Prepare state for update
                const snapshotBeforeUpdate = snapshot.map(snap => snap);
                const releaseSnapshotBeforeUpdate = snapshotBeforeUpdate.retain();
                const currentData = data;
                const currentRows = data.rows;

                // Initialize helpers
                const { getRowIndex, findRow, findRowIndex, getRowAncestors, getSubrowFamily, getSubrows } = createRowHelpers(currentRows);

                // Collect and prepare data for update
                const newParentIndex = !newParentId ? null : getRowIndex(newParentId);
                const rowToMove = currentRows.get(rowId);
                const rowAncestors = getRowRelationsToRoot(rowId, currentRows);
                const subrows = getSubRowsOfRow(rowId, currentRows);
                const subrowFamily = getFamilySubRowsOfRow(rowId, currentRows);

                let error: any = null;
                let isMoveAborted = false;
                const abortMove = () => (isMoveAborted = true);
                try {
                    let newRows: RowsMap;
                    let releaseUpdatedSnapshot: () => void;
                    const updatedSnapshot = await snapshot.asyncMap(async snapshot => {
                        releaseUpdatedSnapshot = snapshot.retain();
                        const { getCellValue, setCellValue } = createCellValueSetGet(snapshot);
                        if (onUpdateRowTree) {
                            newRows = (await onUpdateRowTree({
                                rowsMap: currentRows,
                                rowAncestors,
                                subrowFamily,
                                subrows,
                                rowToMove,
                                newParentIndex,
                                newParentId,
                                getRowIndex,
                                findRowIndex,
                                getRowAncestors,
                                getSubrowFamily,
                                getSubrows,
                                getCellValue,
                                setCellValue,
                                abortMove,
                                findRow,
                            })) as RowsMap;
                        } else {
                            newRows = reduceMap(
                                currentRows,
                                (rows, row, i) => {
                                    if (row.id !== rowId) {
                                        rows.set(row.id, row);
                                        if (i === newParentIndex) {
                                            rows.set(rowToMove.id, { ...rowToMove, parentRowId: newParentId });
                                        }
                                    }
                                    return rows;
                                },
                                new Map() as RowsMap,
                            );
                        }
                    });
                    // const setNextState = async (snapshot: Snapshot, set: CellSetter) => {
                    // 	const getCellValue = createCellValueGetter(snapshot);
                    // 	const setCellValue = createCellValueSetter(set);
                    // 	let newRows: RowsMap;
                    //     if (onUpdateRowTree) {
                    //         newRows = (await onUpdateRowTree({
                    //             rowsMap: currentRows,
                    //             rowAncestors,
                    //             subrowFamily,
                    //             subrows,
                    //             rowToMove,
                    //             newParentIndex,
                    //             newParentId,
                    //             getRowIndex,
                    //             findRowIndex,
                    //             getRowAncestors,
                    //             getSubrowFamily,
                    //             getSubrows,
                    //             getCellValue,
                    //             setCellValue,
                    //             abortMove,
                    //             findRow,
                    //         })) as RowsMap;
                    //     } else {
                    //         newRows = reduceMap(
                    //             currentRows,
                    //             (rows, row, i) => {
                    //                 if (row.id !== rowId) {
                    //                     rows.set(row.id, row);
                    //                     if (i === newParentIndex) {
                    //                         rows.set(rowToMove.id, { ...rowToMove, parentRowId: newParentId });
                    //                     }
                    //                 }
                    //                 return rows;
                    //             },
                    //             new Map() as RowsMap,
                    //         );
                    //     }
                    // 	return newRows
                    // };
                    // const newRows = await setNextState(snapshot, set);
                    const cleanUp = () => {
                        releaseSnapshotBeforeUpdate();
                        releaseUpdatedSnapshot();
                    };
                    if (isMoveAborted) {
                        cleanUp();
                        return;
                    }
                    const newRowAncestors = getRowRelationsToRoot(rowId, newRows);
                    const newSubrowFamily = getFamilySubRowsOfRow(rowId, newRows);
                    const newSubrows = getSubRowsOfRow(rowId, newRows);

                    const onMoveRowInfo: OnMoveRowInfo = {
                        // rowToMove: updatedRowToMove,
                        rowToMove,
                        rowAncestors,
                        newRowAncestors,

                        subrowFamily,
                        newSubrowFamily,

                        subrows,
                        newSubrows,
                    };

                    const undoOnMoveRowInfo: OnMoveRowInfo = {
                        rowToMove,
                        rowAncestors: newRowAncestors,
                        newRowAncestors: rowAncestors,

                        subrowFamily: newSubrowFamily,
                        newSubrowFamily: subrowFamily,

                        subrows: newSubrows,
                        newSubrows: subrows,
                    };

                    const newData: InternalGridData = {
                        ...data,
                        rows: newRows,
                    };
                    setData(newData);
                    gotoSnapshot(updatedSnapshot);
                    addAction(
                        async () => {
                            setData(currentData);
                            gotoSnapshot(snapshotBeforeUpdate);
                            if (onMoveRow) {
                                await onMoveRow(undoOnMoveRowInfo);
                            }
                        },
                        async () => {
                            setData(newData);
                            gotoSnapshot(updatedSnapshot);
                            if (onMoveRow) {
                                await onMoveRow(onMoveRowInfo);
                            }
                        },
                        cleanUp,
                    );
                    if (onMoveRow) {
                        error = await runVoidCallbackAsync(async () => {
                            await onMoveRow(onMoveRowInfo);
                        });
                    }
                } catch (err) {
                    error = err;
                }
                if (error) {
                    throw error;
                }
            },
        [addAction, data, onMoveRow, onUpdateRowTree, setData],
    );

    // const moveRow = useRecoilCallback(
    //     ({ snapshot, set }) =>
    //         async (rowId: string, newParentId: string | null = null) => {
    //             // Prepare state for update
    //             const currentData = data;
    //             const currentRows = data.rows;

    //             // Initialize helpers
    //             const { getRowIndex, findRow, findRowIndex, getRowAncestors, getSubrowFamily, getSubrows } = createRowHelpers(currentRows);

    //             // Collect and prepare data for update
    //             const newParentIndex = !newParentId ? null : getRowIndex(newParentId);
    //             const rowToMove = currentRows.get(rowId);
    //             const rowAncestors = getRowRelationsToRoot(rowId, currentRows);
    //             const subrows = getSubRowsOfRow(rowId, currentRows);
    //             const subrowFamily = getFamilySubRowsOfRow(rowId, currentRows);

    //             let error: any = null;
    //             let isMoveAborted = false;
    //             const abortMove = () => (isMoveAborted = true);
    //             try {

    //                 let newRows: RowsMap;

    //                 if (onUpdateRowTree) {
    //                     const setCellValue = createCellValueSetter(set);
    //                     const getCellValue = createCellValueGetter(snapshot);
    //                     newRows = (await onUpdateRowTree({
    //                         rowsMap: currentRows,
    //                         rowAncestors,
    //                         subrowFamily,
    //                         subrows,
    //                         rowToMove,
    //                         newParentIndex,
    //                         newParentId,
    //                         getRowIndex,
    //                         findRowIndex,
    //                         getRowAncestors,
    //                         getSubrowFamily,
    //                         getSubrows,
    //                         getCellValue,
    //                         setCellValue,
    //                         abortMove,
    //                         findRow,
    //                     })) as RowsMap;
    //                 } else {
    //                     newRows = reduceMap(
    //                         currentRows,
    //                         (rows, row, i) => {
    //                             if (row.id !== rowId) {
    //                                 rows.set(row.id, row);
    //                                 if (i === newParentIndex) {
    //                                     rows.set(rowToMove.id, { ...rowToMove, parentRowId: newParentId });
    //                                 }
    //                             }
    //                             return rows;
    //                         },
    //                         new Map() as RowsMap,
    //                     );
    //                 }

    //                 if (isMoveAborted) {
    //                     return;
    //                 }
    //                 const newRowAncestors = getRowRelationsToRoot(rowId, newRows);
    //                 const newSubrowFamily = getFamilySubRowsOfRow(rowId, newRows);
    //                 const newSubrows = getSubRowsOfRow(rowId, newRows);

    //                 const onMoveRowInfo: OnMoveRowInfo = {
    //                     rowToMove,
    //                     rowAncestors,
    //                     newRowAncestors,

    //                     subrowFamily,
    //                     newSubrowFamily,

    //                     subrows,
    //                     newSubrows,
    //                 };

    //                 const undoOnMoveRowInfo: OnMoveRowInfo = {
    //                     rowToMove,
    //                     rowAncestors: newRowAncestors,
    //                     newRowAncestors: rowAncestors,

    //                     subrowFamily: newSubrowFamily,
    //                     newSubrowFamily: subrowFamily,

    //                     subrows: newSubrows,
    //                     newSubrows: subrows,
    //                 };

    //                 const newData: InternalGridData = {
    //                     ...data,
    //                     rows: newRows,
    //                 };
    //                 setData(newData);
    //                 addAction(
    //                     async () => {
    //                         setData(currentData);
    //                         if (onMoveRow) {
    //                             await onMoveRow(undoOnMoveRowInfo);
    //                         }
    //                     },
    //                     async () => {
    //                         setData(newData);
    //                         if (onMoveRow) {
    //                             await onMoveRow(onMoveRowInfo);
    //                         }
    //                     },
    //                 );
    //                 if (onMoveRow) {
    //                     error = await runVoidCallbackAsync(async () => {
    //                         await onMoveRow(onMoveRowInfo);
    //                     });
    //                 }
    //             } catch (err) {
    //                 error = err;
    //             }
    //             if (error) {
    //                 throw error;
    //             }
    //         },
    //     [addAction, data, onMoveRow, onUpdateRowTree, setData],
    // );

    const allClosed = useMemo(() => {
        return !someMap(rows, row => expandedRows[row.id]);
    }, [expandedRows, rows]);

    // TODO remove this when trimming the api
    const rowsRelations = useMemo(() => mapMapToArray(rows, row => getRowRelationsToRoot(row.id, rows)), [rows]);
    const maxGridDepth = useMemo(() => Math.max(...rowsRelations.map(tree => tree.length)), [rowsRelations]);

    // We always render all rows and let the individual row decide how to
    // handle its visibility. This is more performant because the render tree is static
    const visibleRows = useMemo(() => {
        return mapMapToArray(rows, (row, i): VisibleRow => {
            const subrowsOfRow = getSubRowsOfRow(row.id, rows);
            const rowRelations = rowsRelations[i];
            // Root rows has no parent row id
            const isRootRow = !row.parentRowId;
            // The row is visible if the parent row is expanded, or is a root row
            const visible = isRootRow || Boolean(expandedRows[row.parentRowId]);
            // root rows with no subrows cannot expand
            const expanded = isRootRow && !subrowsOfRow.length ? false : expandedRows[row.id];

            return {
                row,
                visible,
                nestingLevel: rowRelations.length,
                expanded,
                subrows: subrowsOfRow,
                rowRelations,
            };
        });
    }, [expandedRows, rows, rowsRelations]);

    const getUpdatedData = useRecoilCallback(
        ({ snapshot }) =>
            async (): Promise<GridData> => {
                const getCellValue = createCellValueGetter(snapshot);
                const updatedData = await produce(data, async draft => {
                    for (const row of draft.originalRows) {
                        // row.metadata = await snapshot.getPromise(rowMetadataAtoms(row));
                        if (row.columnRows) {
                            for (const columnRow of row.columnRows) {
                                if (columnRow.cells) {
                                    for (const cell of columnRow.cells) {
                                        if (cell !== null) {
                                            const currentValue = await getCellValue(cell);
                                            cell.value = currentValue.toNumber();
                                        }
                                    }
                                }
                            }
                        }
                        if (row.totalColumn?.cells) {
                            for (const totalCell of row.totalColumn.cells) {
                                if (totalCell !== null) {
                                    const currentValue = await getCellValue(totalCell);
                                    totalCell.value = currentValue.toNumber();
                                }
                            }
                        }
                    }
                });
                return {
                    id: updatedData.id,
                    headers: updatedData.headers,
                    rows: Array.from(updatedData.rows.values()), // Use current local state order, preserving drag-and-drop changes
                    metadata: updatedData.metadata,
                    name: updatedData.name,
                };
            },
        [data],
    );

    const getRowsMap = useEvent(() => rows as ImmutableRowsMap);

    const gridMethods = useMemo(() => {
        return {
            addRow,
            updateCellValue,
            getSubRows,
            getRowAncestorTreeToRoot,
            useGridCellState,
            useGridCellValue,
            getUpdatedData,
            moveRow,
            getRowsMap,
            setRootRows,
        };
    }, [addRow, getRowAncestorTreeToRoot, getSubRows, updateCellValue, getUpdatedData, moveRow, getRowsMap, setRootRows]);

    const staticGridData = useMemo(() => {
        return {
            name,
            id,
            metadata,
            headers,
        };
    }, [headers, id, metadata, name]);

    const gridData = useMemo(() => {
        return {
            maxGridDepth,
            allClosed,
            visibleRows,
            rowsRelations,
        };
    }, [allClosed, maxGridDepth, rowsRelations, visibleRows]);

    return useMemo(() => {
        return {
            staticGridData,
            gridData,
            gridMethods,
            expandedRows,
            setExpandedRows,
        };
    }, [expandedRows, gridData, gridMethods, setExpandedRows, staticGridData]);
};

const createRowHelpers = (currentRows: RowsMap) => {
    return {
        getRowIndex: (rowId: string) => findIndexInMap(currentRows, row => row.id === rowId),
        findRowIndex: (cb: (row: GridRow) => boolean) => findIndexInMap(currentRows, cb),
        findRow: (cb: (row: GridRow) => boolean) => findMap(currentRows, cb),
        getSubrowFamily: (rowId: string) => getFamilySubRowsOfRow(rowId, currentRows),
        getSubrows: (rowId: string) => getSubRowsOfRow(rowId, currentRows),
        getRowAncestors: (rowId: string) => getRowRelationsToRoot(rowId, currentRows),
    };
};

class AbortRowOperation extends Error {
    constructor(message?: string) {
        super(message);
        this.name = 'AbortRowOperation';
    }
}
